// Styles
import "./AppMenu.sass";

// Mixins
import Delayable from "@/core/mixins/delayable";
import Dependent from "@/core/mixins/dependent";
import Detachable from "@/core/mixins/detachable";
import Menuable from "@/core/mixins/menuable";
import Returnable from "@/core/mixins/returnable";
import Toggleable from "@/core/mixins/toggleable";

// Directives
import ClickOutside from "@/core/directives/click-outside";
import Resize from "@/core/directives/resize";

// Utilities
import mixins from "@utils/mixins";
import { convertToUnit, keyCodes } from "@utils/helpers";

const baseMixins = mixins(
  Dependent,
  Delayable,
  Detachable,
  Menuable,
  Returnable,
  Toggleable
);

export default baseMixins.extend({
  name: "AppMenu",

  directives: {
    ClickOutside,
    Resize
  },

  props: {
    closeOnClick: {
      type: Boolean,
      default: true
    },
    closeOnContentClick: {
      type: Boolean,
      default: true
    },
    disabled: Boolean,
    disableKeys: Boolean,
    maxHeight: {
      type: [Number, String],
      default: "auto"
    },
    openOnClick: {
      type: Boolean,
      default: true
    },
    offsetX: Boolean,
    offsetY: Boolean,
    openOnHover: Boolean,
    origin: {
      type: String,
      default: "top left"
    },
    transition: {
      type: [Boolean, String],
      default: "app-menu-transition"
    },
    arrow: Boolean,
    customCloseCondition: {
      type: Function,
      default: () => true
    }
  },

  data() {
    return {
      defaultOffset: 8,
      hasJustFocused: false,
      resizeTimeout: 0
    };
  },

  computed: {
    calculatedLeft() {
      const menuWidth = Math.max(
        this.dimensions.content.width,
        parseFloat(this.calculatedMinWidth)
      );

      return this.calcLeft(menuWidth) || "0";
    },
    calculatedMaxHeight() {
      const height = convertToUnit(this.maxHeight);

      return height || "0";
    },
    calculatedMaxWidth() {
      return convertToUnit(this.maxWidth) || "0";
    },
    calculatedMinWidth() {
      if (this.minWidth) {
        return convertToUnit(this.minWidth) || "0";
      }

      const minWidth = Math.min(
        this.dimensions.activator.width + Number(this.nudgeWidth),
        Math.max(this.pageWidth - 24, 0)
      );

      const calculatedMaxWidth = isNaN(parseInt(this.calculatedMaxWidth))
        ? minWidth
        : parseInt(this.calculatedMaxWidth);

      return convertToUnit(Math.min(calculatedMaxWidth, minWidth)) || "0";
    },
    calculatedTop() {
      const top = this.calcTop();
      return top || "0";
    },
    styles() {
      return {
        maxHeight: this.calculatedMaxHeight,
        minWidth: this.calculatedMinWidth,
        maxWidth: this.calculatedMaxWidth,
        top: this.calculatedTop,
        left: this.calculatedLeft,
        transformOrigin: this.origin,
        zIndex: this.zIndex || this.activeZIndex
      };
    },

    classes() {
      return {
        "app-menu__arrow": this.arrow,
        "app-menu__arrow--top": this.arrow && this.top,
        "app-menu__arrow--right": this.arrow && this.right,
        "app-menu__arrow--bottom": this.arrow && this.bottom,
        "app-menu__arrow--left": this.arrow && this.left
      };
    }
  },

  watch: {
    isContentActive(val) {
      this.hasJustFocused = val;
    }
  },

  mounted() {
    this.isActive && this.activate();
  },

  methods: {
    activate() {
      // Update coordinates and dimensions of menu
      // and its activator
      this.updateDimensions();
      // Start the transition
      requestAnimationFrame(() => {
        this.startTransition().then(() => this.$emit("transitioned"));
      });
    },
    calcLeftAuto() {
      return parseInt(this.dimensions.activator.left - this.defaultOffset * 2);
    },
    closeConditional(e) {
      const target = e.target;

      return (
        this.isActive &&
        !this._isDestroyed &&
        this.closeOnClick &&
        !this.$refs.content.contains(target) &&
        this.customCloseCondition(target)
      );
    },
    genTransition() {
      if (!this.transition) return this.genContent();

      return this.$createElement(
        "transition",
        {
          props: {
            name: this.transition
          }
        },
        [this.genContent()]
      );
    },
    genDirectives() {
      const directives = [
        {
          name: "show",
          value: this.isContentActive
        }
      ];

      // Do not add click outside for hover menu
      if (!this.openOnHover && this.closeOnClick) {
        directives.push({
          name: "click-outside",
          value: () => {
            this.isActive = false;
          },
          args: {
            closeConditional: this.closeConditional,
            include: () => [this.$el, ...this.getOpenDependentElements()]
          }
        });
      }

      return directives;
    },
    genContent() {
      const options = {
        attrs: {
          ...this.getScopeIdAttrs(),
          role: "role" in this.$attrs ? this.$attrs.role : "menu"
        },
        staticClass: "app-menu__content",
        class: {
          ...this.classes,
          "app-menu__content--fixed": this.activatorFixed,
          "menuable__content--active": this.isActive,
          [this.contentClass.trim()]: true
        },
        style: this.styles,
        directives: this.genDirectives(),
        ref: "content",
        on: {
          click: e => {
            e.preventDefault();
            const target = e.target;

            if (target.getAttribute("disabled")) return;
            if (this.closeOnContentClick) this.isActive = false;
          },
          keydown: this.onKeyDown
        }
      };

      if (!this.disabled && this.openOnHover) {
        options.on = options.on || {};
        options.on.mouseenter = this.mouseEnterHandler;
      }

      if (this.openOnHover) {
        options.on = options.on || {};
        options.on.mouseleave = this.mouseLeaveHandler;
      }

      return this.$createElement("div", options, this.getContentSlot());
    },
    mouseEnterHandler() {
      this.runDelay("open", () => {
        if (this.hasJustFocused) return;

        this.hasJustFocused = true;
        this.isActive = true;
      });
    },
    mouseLeaveHandler(e) {
      // Prevent accidental re-activation
      this.runDelay("close", () => {
        if (this.$refs.content.contains(e.relatedTarget)) return;

        requestAnimationFrame(() => {
          this.isActive = false;
          this.callDeactivate();
        });
      });
    },
    onKeyDown(e) {
      if (e.keyCode === keyCodes.esc) {
        // Wait for dependent elements to close first
        setTimeout(() => {
          this.isActive = false;
        });
        const activator = this.getActivator();
        this.$nextTick(() => activator && activator.focus());
      } else if (
        !this.isActive &&
        [keyCodes.up, keyCodes.down].includes(e.keyCode)
      ) {
        this.isActive = true;
      }
    },
    onResize() {
      if (!this.isActive) return;

      // Account for screen resize
      // and orientation change
      // eslint-disable-next-line no-unused-expressions
      this.$refs.content.offsetWidth;
      this.updateDimensions();

      // When resizing to a smaller width
      // content width is evaluated before
      // the new activator width has been
      // set, causing it to not size properly
      // hacky but will revisit in the future
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = window.setTimeout(this.updateDimensions, 100);
    },
    genActivatorListeners() {
      const listeners = Menuable.options.methods.genActivatorListeners.call(
        this
      );

      if (!this.disableKeys) {
        listeners.keydown = this.onKeyDown;
      }

      return listeners;
    }
  },

  render(h) {
    const data = {
      staticClass: "app-menu",
      class: {
        "app-menu--attached":
          this.attach === "" || this.attach === true || this.attach === "attach"
      },
      directives: [
        {
          arg: "500",
          name: "resize",
          value: this.onResize
        }
      ]
    };

    return h("div", data, [
      !this.activator && this.genActivator(),
      this.showLazyContent(() => [this.genTransition()])
    ]);
  }
});
